import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    MdBusiness,
    MdEdit,
    MdEmail,
    MdLocationOn,
    MdPhone,
    MdPerson,
    MdCalendarMonth,
    MdSettingsSuggest,
} from 'react-icons/md';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getLocationById, getAllLocations } from '../../Services/LocationService';
import { APPRoutes } from '../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import { toast } from 'react-toastify';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
    Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
    Inactive: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)' },
};

const formatAddress = (address) => {
    if (!address) {
        return '-';
    }

    const pieces = [
        address.street ?? address.Street,
        address.street2 ?? address.Street2,
        address.city ?? address.City,
        address.state ?? address.State,
        address.country ?? address.Country,
        address.zipcode ?? address.Zipcode,
    ].filter(Boolean);

    return pieces.length ? pieces.join(', ') : '-';
};

const getStatusName = (location) => {
    if (!location) return 'Unknown';
    if (location.status_name) return location.status_name;
    if (location.StatusName) return location.StatusName;
    if (typeof location.status_id === 'number') return location.status_id === 1 ? 'Active' : 'Inactive';
    if (typeof location.status_id === 'string') return location.status_id === '1' ? 'Active' : 'Inactive';
    return 'Unknown';
};

export default function LocationView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );

    const canEditLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_LIST, decodedToken);

    const [locationData, setLocationData] = React.useState(location.state?.location ?? null);
    const [loading, setLoading] = React.useState(!Boolean(location.state?.location));

    const loadLocationData = async () => {
        if (!token) return;

        try {
            setLoading(true);
            let targetId = id;
            if (!targetId || targetId === 'first' || targetId === ':id') {
                const listRes = await getAllLocations(token);
                const rawList = listRes?.data?.locations || listRes?.data?.Locations || listRes?.data || listRes;
                const list = Array.isArray(rawList) ? rawList : [];
                targetId = list[0]?.location_id || list[0]?.LocationId || list[0]?.id || list[0]?.Id || null;
            }
            if (!targetId) {
                setLocationData(null);
                return;
            }
            const response = await getLocationById(targetId, token);
            if (response?.is_success) {
                setLocationData(response?.data ?? null);
            } else if (response?.is_success === false && response?.status_code === 500) {
                toast.error('Internal Server Error. Please Contact Support Team.');
                setLocationData(null);
            } else {
                toast.error(response?.message || 'Failed to load location.');
                setLocationData(null);
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Failed to load location.');
            setLocationData(null);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (!token) return;
        if (!locationData) {
            loadLocationData();
        }
    }, [id, locationData, token]);

    const statusName = getStatusName(locationData);
    const createdOnLocal = React.useMemo(
        () => formatUtcToLocalDateTime(locationData?.created_on ?? locationData?.CreatedOn),
        [locationData?.created_on, locationData?.CreatedOn],
    );
    const modifiedOnLocal = React.useMemo(
        () => formatUtcToLocalDateTime(locationData?.modified_on ?? locationData?.ModifiedOn),
        [locationData?.modified_on, locationData?.ModifiedOn],
    );
    const contactEmail = locationData?.email ?? locationData?.Email ?? '-';
    const contactPhone = locationData?.phone ?? locationData?.Phone ?? '-';
    const contactFax = locationData?.fax ?? locationData?.Fax ?? '-';
    const organizationName = locationData?.organization_name ?? locationData?.OrganizationName ?? '-';
    const accountName = locationData?.account_name ?? locationData?.AccountName ?? '-';
    const createdBy = locationData?.created_by ?? locationData?.CreatedBy ?? '-';
    const modifiedBy = locationData?.modified_by ?? locationData?.ModifiedBy ?? '-';

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                    <div className="skeleton position-relative overflow-hidden rounded-pill" style={{ height: 40, width: 110 }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 120, borderRadius: 10 }} />
                </div>

                <div className="p-3 p-lg-4 mb-4 hc-surface overflow-hidden">
                    <div className="row g-4 g-xl-5 align-items-stretch">
                        <div className="col-xl-9">
                            <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4 h-100">
                                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 124, height: 124, borderRadius: 28 }} />

                                <div className="flex-grow-1 w-100">
                                    <div className="skeleton mb-3 rounded-3" style={{ height: 28, width: '65%' }} />
                                    <div className="skeleton mb-2 rounded-3" style={{ height: 20, width: '45%' }} />
                                    <div className="skeleton mb-2 rounded-3" style={{ height: 20, width: '40%' }} />
                                    <div className="skeleton position-relative overflow-hidden rounded-3" style={{ height: 24, width: '30%' }} />
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3">
                            <div className="skeleton h-100 rounded-4" style={{ minHeight: 220 }} />
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-lg-6">
                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 180, borderRadius: 20 }} />
                    </div>
                    <div className="col-lg-6">
                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 180, borderRadius: 20 }} />
                    </div>

                    <div className="col-12">
                        <div className="p-4 p-lg-5 hc-surface">
                            <div className="skeleton mb-4" style={{ height: 34, width: 250, borderRadius: 10 }} />
                            <div className="row g-3">
                                {[1, 2, 3, 4, 5, 6].map((item) => (
                                    <div className="col-12 col-md-4" key={item}>
                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 108, borderRadius: 12 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!locationData) {
        return (
            <ViewEmptyState
                title="No Locations Available"
                description="There are no location records available to display."
                icon={MdLocationOn}
                canViewList={canViewList}
                backRoute={APPRoutes.LOCATIONS}
                backLabel="Back to Locations"
            />
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton onClick={() => navigate(APPRoutes.LOCATIONS)} />
                )}

                {canEditLocation ? (
                    <button
                        type="button"
                        className="btn hc-bg-primary text-white d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(APPRoutes.LOCATIONS_EDIT.replace(':id', id))}
                    >
                        <MdEdit size={18} /> Edit Location
                    </button>
                ) : null}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <span
                                className="px-3 py-2 rounded-pill fw-semibold"
                                style={{
                                    fontSize: '0.82rem',
                                    backgroundColor: STATUS_STYLE[statusName]?.backgroundColor || 'var(--hc-status-neutral-bg)',
                                    color: STATUS_STYLE[statusName]?.color || 'var(--hc-table-footer-text)',
                                }}
                            >
                                {statusName}
                            </span>
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {locationData.name || '-'}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)' }}>
                            <MdBusiness size={14} className="me-1" />{organizationName}
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                            <MdSettingsSuggest size={14} className="me-1" />{accountName}
                        </p>
                    </div>

                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <div
                                    className="d-flex align-items-center justify-content-center overflow-hidden rounded-3 flex-shrink-0"
                                    style={{
                                        width: 56,
                                        height: 56,
                                        background: 'rgba(var(--hc-white-rgb), 0.15)',
                                    }}
                                >
                                    <MdLocationOn size={24} className="text-white" />
                                </div>
                                <div>
                                    <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                        Address Overview
                                    </p>
                                    <p className="fw-bold text-white mb-0" style={{ fontSize: '1.55rem', lineHeight: 1.1 }}>
                                        {locationData.address_id ?? locationData.address?.id ?? 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Current site address and operational location details.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-xl-8 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary flex-fill d-flex flex-column" style={{ minHeight: 220 }}>
                        <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
                            <MdBusiness size={22} className="hc-text-primary" /> Location Address
                        </h5>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                    <div className="small text-muted">Street</div>
                                    <div className="fw-semibold text-dark">{locationData?.address?.street ?? locationData?.address?.Street ?? '-'}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                    <div className="small text-muted">Street 2</div>
                                    <div className="fw-semibold text-dark">{locationData?.address?.street2 ?? locationData?.address?.Street2 ?? '-'}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                    <div className="small text-muted">City</div>
                                    <div className="fw-semibold text-dark">{locationData?.address?.city ?? locationData?.address?.City ?? '-'}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                    <div className="small text-muted">State</div>
                                    <div className="fw-semibold text-dark">{locationData?.address?.state ?? locationData?.address?.State ?? '-'}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                    <div className="small text-muted">Country</div>
                                    <div className="fw-semibold text-dark">{locationData?.address?.country ?? locationData?.address?.Country ?? '-'}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                    <div className="small text-muted">Zipcode</div>
                                    <div className="fw-semibold text-dark">{locationData?.address?.zipcode ?? locationData?.address?.Zipcode ?? '-'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-4 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary w-100 h-100 d-flex flex-column" style={{ minHeight: 220 }}>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 hc-text-primary">
                                <MdLocationOn size={22} /> Location Details
                            </h5>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                <div className="small text-muted">Phone</div>
                                <div className="fw-semibold text-dark">{contactPhone}</div>
                            </div>
                            <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                <div className="small text-muted">Fax</div>
                                <div className="fw-semibold text-dark">{contactFax}</div>
                            </div>
                            <div className="h-100 d-flex flex-column gap-2 p-3 rounded-4 bg-light border">
                                <div className="small text-muted">Organization</div>
                                <div className="fw-semibold text-dark">{organizationName}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary d-flex flex-column" style={{ minHeight: 220 }}>
                        <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 hc-text-primary">
                            <MdPerson size={22} className="hc-text-primary" /> Audit Info
                        </h5>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                                        <MdPerson size={18} className="hc-text-primary" />
                                    </span>
                                    <div className="text-start">
                                        <div className="small text-muted">Created By</div>
                                        <div className="fw-medium text-dark">{createdBy}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                                        <MdCalendarMonth size={18} className="hc-text-primary" />
                                    </span>
                                    <div className="text-start">
                                        <div className="small text-muted">Created On</div>
                                        <div className="fw-medium text-dark">{createdOnLocal}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                                        <MdPerson size={18} className="hc-text-primary" />
                                    </span>
                                    <div className="text-start">
                                        <div className="small text-muted">Modified By</div>
                                        <div className="fw-medium text-dark">{modifiedBy}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
                                        <MdCalendarMonth size={18} className="hc-text-primary" />
                                    </span>
                                    <div className="text-start">
                                        <div className="small text-muted">Modified On</div>
                                        <div className="fw-medium text-dark">{modifiedOnLocal}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
